import { StandardEvents } from '@shopify/events';

// Add to cart: when an add goes through, a few small petals drift up from the
// button that was pressed and fade. The look is .pp-burst in
// assets/prebbleton.css; this file only says when, and from which button.
//
// Nothing is wrapped or replaced. Horizon's product form already announces
// every add it sends (assets/product-form.js): the event leaves the form
// before the request does and carries a promise that settles once the cart
// has answered, so the petals wait on that promise and stay away from an add
// the shop refused. That one event covers the variety form, the dispatch date
// form and the wholesale box, which are all that form.
//
// A pay row on Pick Your Own is a plain form with no script behind it: the
// browser posts it and the page becomes the cart. There is no answer to wait
// for on this page, so there the petals leave as the form is sent.
//
// "Book now" is the booking app's own button and adds nothing, and a booking
// made in the calendar reports its add from the calendar's box, not from a
// product form (assets/booking-calendar.js). Neither is heard here.

const PETALS = 4;
const FORM = 'product-form-component';
const NATIVE_FORM = 'form[action*="/cart/add"]';
const SUBMIT = 'button[type="submit"], button[name="add"]';
const STICKY_BAR = 'sticky-add-to-cart';

// Long enough for the last petal to land if its animation never reports.
const CLEAR_AFTER = 2000;

const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

/**
 * The button under the visitor's last real press. The sticky bar presses the
 * form's own button from a script, so the form cannot say which of the two the
 * visitor touched; the press itself can.
 * @type {HTMLElement | null}
 */
let pressed = null;

document.addEventListener(
  'click',
  (event) => {
    if (!event.isTrusted) return;
    pressed = event.target instanceof Element ? event.target.closest('button') : null;
  },
  true
);

/** @param {Element | null} element */
function isShowing(element) {
  if (!(element instanceof HTMLElement) || !element.isConnected) return false;
  const box = element.getBoundingClientRect();
  return box.width > 0 && box.height > 0;
}

/**
 * The button the petals leave from: the one the browser names, the one that
 * was pressed if it is the form's own or the sticky bar's, or else the form's
 * first. A press on anything else, the quantity stepper say, is not the add.
 * @param {Element} form
 * @param {Element | null} [submitter]
 */
function sourceButton(form, submitter) {
  if (isShowing(submitter ?? null)) return /** @type {HTMLElement} */ (submitter);

  if (pressed && isShowing(pressed)) {
    const isFormButton = form.contains(pressed) && pressed.matches(SUBMIT);
    if (isFormButton || pressed.closest(STICKY_BAR)) return pressed;
  }

  return Array.from(form.querySelectorAll(SUBMIT)).find(isShowing) ?? null;
}

/**
 * The petals are copies of the flower the header's cart badge already holds,
 * so the shape is still drawn in one place, snippets/petal-marker.liquid.
 * @param {Element | null} button
 */
function burst(button) {
  if (reducedMotion.matches || !isShowing(button)) return;

  const flower = document.querySelector('svg.pp-petal');
  if (!flower) return;

  // Laid over the button and fixed to the window: nothing in the page moves
  // and the page cannot grow a scrollbar for it.
  const box = /** @type {HTMLElement} */ (button).getBoundingClientRect();
  const layer = document.createElement('span');
  layer.className = 'pp-burst';
  layer.setAttribute('aria-hidden', 'true');
  layer.style.insetBlockStart = `${box.top}px`;
  layer.style.insetInlineStart = `${box.left}px`;
  layer.style.inlineSize = `${box.width}px`;

  for (let index = 0; index < PETALS; index++) {
    const petal = /** @type {SVGElement} */ (flower.cloneNode(true));
    petal.setAttribute('class', 'pp-burst__petal');
    petal.removeAttribute('width');
    petal.removeAttribute('height');
    layer.append(petal);
  }

  const clear = () => layer.remove();
  const timer = setTimeout(clear, CLEAR_AFTER);
  let landed = 0;

  layer.addEventListener('animationend', () => {
    landed += 1;
    if (landed < PETALS) return;
    clearTimeout(timer);
    clear();
  });

  document.body.append(layer);
}

document.addEventListener(StandardEvents.cartLinesUpdate, (event) => {
  const update = /** @type {Event & { action?: string, promise?: Promise<{ detail?: { didError?: boolean } }> }} */ (
    event
  );
  if (update.action !== 'add' || !update.promise) return;

  const form = event.target instanceof Element ? event.target.closest(FORM) : null;
  if (!form) return;

  const button = sourceButton(form);

  update.promise.then(
    (result) => {
      if (result?.detail?.didError) return;
      burst(button);
    },
    () => {}
  );
});

document.addEventListener('submit', (event) => {
  const form = event.target;
  if (!(form instanceof HTMLFormElement) || !form.matches(NATIVE_FORM)) return;
  if (event.defaultPrevented || form.closest(FORM)) return;

  burst(sourceButton(form, event.submitter));
});
